import type { CampaignBrand } from '@/features/campaigns/types';

export const authKeys = {
  all: ['auth'] as const,
  roles: () => [...authKeys.all, 'roles'] as const,
};

export const productDataKeys = {
  all: ['product-data'] as const,
  single: (orderNumber: string) => [...productDataKeys.all, 'single', orderNumber] as const,
  batch: (orderNumbers: string[]) => [...productDataKeys.all, 'batch', orderNumbers] as const,
  extract: () => [...productDataKeys.all, 'extract'] as const,
  briefing: (brandKey: string, bu: string, file: string) =>
    [...productDataKeys.all, 'briefing', brandKey, bu, file] as const,
  images: (orderNumber: string) => [...productDataKeys.all, 'images', orderNumber] as const,
};

export const categoryKeys = {
  all: ['categories'] as const,
  search: (query: string, brandType: string) =>
    [...categoryKeys.all, 'search', query, brandType] as const,
  withImages: (query: string, brandType: string) =>
    [...categoryKeys.all, 'with-images', query, brandType] as const,
  images: (categoryId: string) => [...categoryKeys.all, 'images', categoryId] as const,
};

export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...productKeys.lists(), filters] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (id: string) => [...productKeys.details(), id] as const,
  doc: (id: string) => [...productKeys.all, 'doc', id] as const,
};

export const ecommerceKeys = {
  all: ['ecommerce'] as const,
  lists: () => [...ecommerceKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...ecommerceKeys.lists(), filters] as const,
  details: () => [...ecommerceKeys.all, 'detail'] as const,
  detail: (id: string) => [...ecommerceKeys.details(), id] as const,
};

export const campaignKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...campaignKeys.lists(), filters] as const,
  details: () => [...campaignKeys.all, 'detail'] as const,
  detail: (id: string) => [...campaignKeys.details(), id] as const,
  /** Brand-scoped reference data, served outside the campaigns route. */
  promotionMechanisms: (brandType: CampaignBrand) =>
    [...campaignKeys.all, 'promotion-mechanisms', brandType] as const,
  /**
   * The catalogues are campaign-aware — the effective specs and the Website
   * layout both depend on the campaign's mechanism — so they hang off the
   * campaign key and campaign invalidation drops them.
   */
  reusableBlocks: (id: string, templateName: string) =>
    [...campaignKeys.all, 'reusable-blocks', id, templateName] as const,
  websiteSections: (
    id: string,
    brandType: CampaignBrand | undefined,
    promotionMechanism: string | null,
  ) => [...campaignKeys.all, 'website-sections', id, brandType, promotionMechanism] as const,
};

export const freetextKeys = {
  all: ['freetext'] as const,
  lists: () => [...freetextKeys.all, 'list'] as const,
  list: () => [...freetextKeys.lists()] as const,
  details: () => [...freetextKeys.all, 'detail'] as const,
  detail: (id: string) => [...freetextKeys.details(), id] as const,
};

export const templateKeys = {
  all: ['templates'] as const,
  prompts: () => [...templateKeys.all, 'prompts'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (businessUnitId: string, brandType: string, promotionMechanism?: string) =>
    [...templateKeys.lists(), businessUnitId, brandType, promotionMechanism ?? ''] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (businessUnitId: string, id: string) =>
    [...templateKeys.details(), businessUnitId, id] as const,
  blocks: (templateName: string) => [...templateKeys.all, 'blocks', templateName] as const,
};

export const translationKeys = {
  all: ['translations'] as const,
  languages: () => [...translationKeys.all, 'languages'] as const,
  statuses: () => [...translationKeys.all, 'statuses'] as const,
  my: () => [...translationKeys.all, 'my'] as const,
};

export const tradosKeys = {
  all: ['trados'] as const,
  languages: () => [...tradosKeys.all, 'languages'] as const,
  myTranslations: () => [...tradosKeys.all, 'my-translations'] as const,
};

export const sapMcKeys = {
  all: ['sap-mc'] as const,
  statuses: () => [...sapMcKeys.all, 'statuses'] as const,
  my: () => [...sapMcKeys.all, 'my'] as const,
};

export const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationKeys.details(), id] as const,
};
